<script lang="ts" setup>
	const { observeElementInViewport } = useInterSector()
	const inView = shallowRef('introduce')
	const percentage = ref(10)
	const sections = ref<null | HTMLElement[]>(null)
	const contents = [
		{
			title: 'Introduction',
			id: 'introduce',
			sub_title: 'Dear Valued Customer,',
			content: `<br />
									Yes you! <br /><br />
									Come closer because I have got good news that'll make you grin from ear to ear.
									Let's take a quick trip down memory lane, back to when managing your plans and
									navigating your dashboard was already a smooth experience, but we knew it could be
									even better.
									<br /><br />
									With you in mind, we took a step back and reimagined everything from the ground up.
									MCG Version 2.0 isn't just an upgrade; it’s a complete transformation of how
									insurance should work for you. We've made it simpler, more transparent, and a part
									of your everyday life. We have done a lot more, and it’s time to share with you.`
		},
		{
			title: 'Simplified Sign-Up Process',
			id: 'simplified',
			content: ` <br /> We know your time is precious, so we’ve streamlined the sign-up process to get you up and running in no time. <br />  <br /> Our user-friendly steps ensure that you can start enjoying the benefits of MCG quickly and effortlessly.`,
			img: '/v1727082833/simplified_dzvqld.gif'
		},
		{
			title: 'Flexible Plan Management',
			id: 'flexible',
			img: '/v1727082823/flexi-plans_y3nzei.gif',
			content: `Life is full of changes, and your insurance should be able to keep up. <br/>With our new platform, managing your plans is easier than ever.<br/> <br/>Need to upgrade, downgrade, or edit your coverage? No problem. <br/><br/>You can even manage staff members or beneficiaries directly from your account. We put the power in your hands.`
		},
		{
			title: 'Plan Usage Insights',
			id: 'plan-usage',
			img: '/v1727082824/plan-usage_u5ki7s.gif',
			content: `It's not just about buying a plan; it's about how you are utilizing the plan.  <br/><br/>Our new analytics feature is designed to break down your plan usage, giving you deep insights into your insurance experience. <br/><br/>With these detailed insights, you can make informed decisions and maximise your benefits, ensuring that you’re getting the most out of your coverage.`
		},
		{
			title: 'Easy Claims Process',
			id: 'easy-claims',
			img: '/v1727082836/easy-claims_pccax6.gif',
			content: `We have made the claims process as straightforward as possible. <br/><br/>Whether you’re filing a claim for auto, gadget, or travel insurance, our AI-powered system ensures you go all digital through the quick settlement process, getting you back on track with minimal hassle. <br/><br/>The happy tea moment is the direct payment of your claim offer into your withdrawable wallet.`
		},
		{
			title: 'Updated Telemedicine Services',
			id: 'telemedicine-service',
			img: '/v1727082821/updated-telemedicine_qsu2xu.gif',
			content: `Health issues don’t always wait for office hours. <br/><br/>With our introduced tele-chat option coupled with the existing toll-free call, you can consult with a doctor from the comfort of your home, get prescriptions, and even have your medications delivered to your doorstep or the nearest pharmacy.`
		},
		{
			title: 'Hospital locator',
			id: 'hospital-locator',
			img: '/v1727082822/hospital-locator_p6maop.gif',
			content: `Need to find a hospital quickly? <br/><br/>Our integrated map feature helps you locate nearby hospitals and choose the best one based on user ratings. <br/><br/>You can also schedule a hospital visitation date, ensuring you are constantly reminded of your visit days. <br/><br/>Plus, our customer support is just a tap away, ready to assist you during your visit.`
		},
		{
			title: 'Smart Renewals',
			id: 'smart-renewals',
			img: '/v1727082833/smart-renewals_tgce76.gif',
			content: `Never worry about missing a renewal date again. <br/><br/>With our smart renewal system, you can fund your wallet and have your premiums automatically deducted, ensuring continuous coverage without lifting a finger.`
		},
		{
			title: 'Create roles & assign permissions',
			id: 'create-roles',
			img: '/v1727082820/create-role_yujkth.gif',
			content: `As a genius, you do not have to do everything yourself. <br/><br/>That is why we introduced delegate like a genius: <br/><br/>Are you a business owner? <br/>Now you can invite other team members for real time management; HR, head of operations, finance, etc.`
		},
		{
			title: 'Conclusion',
			id: 'conclusion',
			content: `You shared your thoughts, and we listened. <br/><br/>We took it a lot further to provide premium design flows and introduced new features that would improve performance and enhance your experience. <br/><br/>With you in mind, we took a step back and reimagined everything from the ground up. <br/><br/>MCG Version 2.0 isn't just an upgrade; it’s a complete transformation of how insurance should work for you. <br/><br/>We’ve made it simpler, more transparent, and a part of your everyday life.`
		}
	]
	onMounted(() => {
		if (sections.value && Array.isArray(sections.value)) {
			contents.forEach((item, index) => {
				document.addEventListener('scroll', () => {
					const title = document.querySelector(`#${item.id}`)
					if (title) {
						observeElementInViewport(title, isVisible => {
							if (isVisible) {
								inView.value = item.id
								percentage.value = (contents.length / 10) * (index + 1) * 10
							}
						})
					}
				})
			})
		}
	})
</script>

<template>
	<div>
		<header class="sm:min-h-[627px] bg-base-800 w-full">
			<ui-pad
				:ui="{ paddingInline: 'sm:px-0 px-5', paddingBlock: 'pt-20' }"
				class="grid lg:grid-cols-2 gap-x-16 gap-y-16 items-center justify-between flex-wrap w-full"
			>
				<div class="sm:px-8 space-y-8">
					<h3
						class="sm:text-[4em] maitree-bold text-[1.5em] text-white xs:text-[2.5em] font-[700] sm:leading-[83.2px]"
					>
						Alas! <br />
						Your dashboard just got <span class="text-[#FFB908]">smarter</span>. 🎉
					</h3>

					<p class="font-medium sm:text-xl sm:leading-[32px] text-base text-white mb-16 md:mb-0">
						Unlock new possibilities with our fully upgraded dashboard. It's faster, smarter, and
						transformed just for you.
					</p>
				</div>
				<nuxt-img
					loading="lazy"
					provider="cloudinary"
					src="/v1727082913/guide_ggmjwg.png"
					alt="guide"
					class="max-h-[619.89px]"
				/>
			</ui-pad>
		</header>

		<ui-pad :ui="{ margin: 'mt-10', paddingInline: 'sm:px-10 px-0' }">
			<div class="flex realtive items-stretch justify-between">
				<div
					class="border-r border-gray-300 invisible-scrollbar lg:block hidden sticky max-h-screen space-y-12 pt-8 top-32 overflow-y-auto lg:pb-24 flex-1 max-w-[350px] left-0"
				>
					<ui-progress :percentage="percentage" class="hidden lg:block max-w-80 sticky top-0 bg-current" />
					<ul class="space-y-5 h-full">
						<li
							v-for="(item, index) in contents"
							:key="index"
							class="border-l-[6px] border-transparent transition py-3 rounded-md pl-5"
							:class="[item.id === inView ? 'active' : '!text-gray-700']"
						>
							<nuxt-link
								:to="`#${item.id}`"
								:class="[item.id === inView ? '!text-main' : '!text-gray-700']"
								replace
							>
								{{ item.title }}
							</nuxt-link>
						</li>
					</ul>
				</div>

				<div class="flex-1 flex flex-col justify-center">
					<ui-progress
						:percentage="percentage"
						class="block lg:hidden max-w-[720px] mx-auto w-full py-10 sticky top-[56px] left-0"
					/>
					<div class="max-w-[720px] mx-auto px-5 py-10 w-full sm:p-10 bg-white border border-gray-200">
						<section
							v-for="(item, index) in contents"
							:key="index"
							class="py-10 sm:py-20 snap-start"
							ref="sections"
						>
							<h3
								class="font-[700] mb-3 sm:text-[2em] sm:leading-[38.4px] text-[1.5em] leading-[28.8px] pt-5 text-blue-950"
								:id="item.id"
							>
								{{ item.title }}
							</h3>

							<div>
								<div
									v-if="item?.img"
									class="max-h-[370px] w-full px-5 pt-5 my-5 bg-base-400 rounded-lg justify-center items-center flex"
								>
									<nuxt-img
										provider="cloudinary"
										class="object-cover w-full max-h-[357px] rounded-xl"
										:src="item.img"
										loading="lazy"
										alt="guide"
									/>
								</div>
								<span class="text-slate-700 text-lg sm:text-2xl font-semibold sm:leading-[38.40px]">
									{{ item.sub_title }}
								</span>
								<br />

								<p
									class="text-slate-700 text-lg sm:text-xl font-medium leading-[32px] sm:leading-loose"
									v-html="item.content"
								/>
							</div>
						</section>
					</div>
				</div>
			</div>
		</ui-pad>
	</div>
</template>

<style scoped>
	.active {
		@apply font-semibold text-base border-main;
	}
</style>
